import type { ListNotificationsResponse } from '@nadoutong/contracts';
import { platformApiRequest } from './client.ts';

/**
 * Typed wrapper around platform-api `GET /v1/notifications`
 * (tenant #116 phase 1).
 *
 * Cursor discipline, verified the hard way on the desktop client
 * (`packages/contracts/src/public-notifications.ts`): `since` is the
 * max `lastSeenAt` seen so far — a MICROSECOND-precision string
 * (6 fractional digits). It must travel as the exact string the server
 * issued. A `Date` round-trip truncates it to milliseconds, which puts
 * the cursor BEFORE the newest row and makes that row repeat on every
 * sync forever. This module therefore only ever forwards the string.
 *
 * The server filters strictly-greater-than on `last_seen_at` and
 * returns ascending; `limit` is clamped 1..200 server-side.
 */

export interface ListNotificationsParams {
  /** Raw cursor string, forwarded verbatim. Omit for the first sync. */
  readonly since?: string;
  readonly limit: number;
}

export async function listNotifications(
  params: ListNotificationsParams,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListNotificationsResponse> {
  const query = new URLSearchParams();
  query.set('limit', String(params.limit));
  if (params.since !== undefined && params.since.length > 0) {
    query.set('since', params.since);
  }
  return platformApiRequest<ListNotificationsResponse>(
    `/v1/notifications?${query.toString()}`,
    { signal },
    fetchImpl,
  );
}
